import {
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/outline';
import { Button, Divider, Input, Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import confetti from 'canvas-confetti';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { CartItem } from '@/utils/interfaces/cart';
import { formatPrice } from '@/utils/Product';
import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import ButtonDefault, { ButtonVariants, Spinner } from '../shared/basic/button';
import { ShouldRender } from '../shared/basic/ShouldRender';
import { trackBeginCheckoutCartEvent } from '../shared/Gtags';
import { parseFormattedNumber } from '../shared/parseFormattedNumber';
import { cartAtom } from '../../atoms/Cart';
import { user } from '../../atoms/user';
import { User } from '../../utils/interfaces/User';

interface Props {
  amount: {
    subTotal: number;
    discount: string;
    total: number;
    tax: number;
    actualCartTotal: number;
    currency?: string;
    currencyCode?: string;
    couponCode: { coupon_code: string }[];
  };
  setCurrentStep: (value: number) => void;
  handleAddCoupon: (value: string) => void;
  removeCoupon: (value: string) => void;
  removeLoading: boolean;
  couponMessage: { message: string; type: string };
  loading: boolean;
  couponApplyLoading: { value: boolean; isSuccess: boolean };
  productsData: CartItem[];
  setCouponMessage: (value: { message: string; type: string }) => void;
}

export const showLoading = (value: any, loading: boolean) => {
  if (loading) {
    return (
      <>
        <Skeleton.Input
          active
          size='small'
          style={{ width: '60%' }}
          className='h-5'
        />
      </>
    );
  } else {
    return <>{value}</>;
  }
};

const Step_1_OrderCard: React.FC<Props> = ({
  amount,
  setCurrentStep,
  handleAddCoupon,
  couponMessage,
  removeCoupon,
  removeLoading,
  loading,
  couponApplyLoading,
  productsData,
  setCouponMessage,
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isBelow370, setIsBelow370] = useState(false);
  const [showCheckIcon, setShowCheckIcon] = useState(false);
  const cartData = useRecoilValue(cartAtom);
  const screens = useBreakpoint();
  const userData: User | undefined = useRecoilValue(user);
  // const ShippingPrice = 199;
  const router = useRouter();
  const handleScrollToBottom = () => {
    if (typeof window !== undefined) {
      programmaticScrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const handleResize = () => setIsBelow370(window.innerWidth < 370);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalItems = useMemo(() => {
    return productsData.reduce((sum, item) => sum + (item?.quantity || 0), 0);
  }, [productsData]);

  const parseNumber = (str: string) => parseFloat(str?.replace(/,/g, ''));

  const totalNumber = parseNumber(amount.total?.toString());

  const savings = -(totalNumber - amount.actualCartTotal);
  const savingsPercentage = isFinite((savings / amount.actualCartTotal) * 100)
    ? Math.round((savings / amount.actualCartTotal) * 100)
    : 0;

  const filteredCoupon =
    amount?.couponCode?.filter(
      (coupon, index, self) =>
        index === self.findIndex((c) => c.coupon_code === coupon.coupon_code),
    ) ?? [];
  const handleClick = () => {
    if (handleAddCoupon) {
      handleAddCoupon(couponCode);
      setCouponCode('');
    }
  };

  useEffect(() => {
    if (couponMessage.type === 'success') {
      setShowCheckIcon(true);
      const confettiOrigin = screens.lg
        ? { x: 0.8, y: 0.5 }
        : { x: 0.5, y: 0.8 };

      confetti({
        particleCount: 30,
        spread: 70,
        origin: confettiOrigin,
        colors: ['#ffffff', '#FFD700', '#FF69B4', '#87CEEB', '#32CD32'],
      });

      const timer = setTimeout(() => {
        setShowCheckIcon(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [couponMessage.type, screens.lg]);

  return (
    <>
      <div className='flex flex-col h-full lg:h-auto w-full'>
        <div className='w-full lg:pt-7 lg:px-0'>
          <div>
            <Input
              className='md:border-[2px] text-sm placeholder:text-xs text-white placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray customInputBlack py-1  bg-bgColor'
              suffix={
                <div
                  className={clsx(
                    couponMessage.type === 'error' && 'shaking-animation',
                  )}
                >
                  <div className='relative'>
                    <Button
                      onClick={handleClick}
                      className={clsx(
                        couponCode
                          ? 'bg-white text-black font-semibold'
                          : 'bg-cartBgColor text-white',
                        'tracking-wider h-9 font-medium border-none px-6 md:px-8 text-xs',
                      )}
                    >
                      {couponApplyLoading.value ? (
                        <span className='transition-opacity duration-300 ease-in-out opacity-100 translate-x-1'>
                          <Spinner />
                        </span>
                      ) : (
                        <>
                          {showCheckIcon ? (
                            <CheckIcon className='text-white w-5 transition-transform duration-300 ease-in-out transform scale-100' />
                          ) : (
                            <span className='transition-opacity duration-300 ease-in-out opacity-100'>
                              APPLY
                            </span>
                          )}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              }
              style={{
                background: '#000000',
              }}
              placeholder='Enter your coupon code'
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
            />
          </div>
          <ShouldRender check={couponMessage.message}>
            <span
              className={`${
                couponMessage.type === 'error'
                  ? 'text-red-500'
                  : 'text-green-600 '
              } pt-2 text-sm w-[90%] flex items-center gap-x-1`}
            >
              <ShouldRender check={couponMessage.type === 'error'}>
                <ExclamationCircleIcon className='w-4 h-4 mt-1' />
              </ShouldRender>
              <ShouldRender check={couponMessage.type === 'success'}>
                <CheckCircleIcon className='w-4 h-4' />
              </ShouldRender>{' '}
              <span className={clsx(couponMessage.type === 'error' && 'pt-1')}>
                {couponMessage.message}
              </span>
            </span>
          </ShouldRender>
        </div>
        {/* <ShouldRender check={screens.md}>
          <ShouldRender
            check={!cartData?.every((item) => item.type === 'digital')}
          >
            <div className='bg-[#2C2302] p-3 mt-4 xl:mt-6 rounded-lg '>
              <span className=' text-xs md:text-sm p-1 md:tracking-wider text-white gap-x-1'>
                Shipping cost:
                <span className='inline-flex items-baseline'>
                  <ShouldRender
                    check={getGlobalCountry()?.name?.toLowerCase() === 'india'}
                  >
                    <b className='line-through px-1 font-normal'>
                      {amount?.currency}199
                    </b>
                  </ShouldRender>
                  <b className='text-[#FFC700] font-normal'>Free Shipping</b>
                </span>
              </span>
            </div>
          </ShouldRender>
        </ShouldRender> */}
        <div
          className={clsx(
            !screens.lg && 'mb-28',
            'bg-cartModalFormBg  rounded-lg mt-5 pt-5 md:pt-8 ',
          )}
        >
          <div className='flex flex-col gap-y-3.5 md:gap-y-5   pb-5'>
            <div className='flex justify-between px-6 text-sm'>
              <span className='text-[#6E6E6E] font-medium'>Cart Total</span>

              {showLoading(
                <>
                  <span className='font-semibold text-white text-base'>
                    {amount?.currency} {formatPrice(amount?.actualCartTotal)}
                  </span>
                </>,
                loading,
              )}
            </div>

            <div className='mx-6  border-b border-dropDownMenuBg border-2 border-dashed'></div>
            <ShouldRender check={amount.discount !== '0.00' && !loading}>
              {filteredCoupon.map((coupon) => (
                <span
                  key={coupon.coupon_code}
                  className='flex justify-between px-6'
                >
                  <span className='text-[#6E6E6E] text-sm font-semibold'>
                    {' '}
                    Coupon Applied ({coupon.coupon_code}){' '}
                  </span>

                  <ButtonDefault
                    size={0}
                    variant={ButtonVariants.UNSTYLED}
                    loading={removeLoading}
                    disabled={removeLoading}
                    onClick={() => {
                      if (removeCoupon) {
                        removeCoupon(coupon.coupon_code);
                      }
                    }}
                    className='text-primary font-semibold uppercase leading-6 text-xs hover:bg-bgColor hover:text-red-400'
                  >
                    Remove
                  </ButtonDefault>
                </span>
              ))}
              <span className='text-[#6E6E6E] font-medium  px-6 flex justify-between'>
                Coupon Code Discount{' '}
                <p className='text-white font-semibold'>
                  - {amount?.currency} {amount.discount}
                </p>
              </span>
            </ShouldRender>

            <ShouldRender
              check={!cartData?.every((item) => item.type === 'digital')}
            >
              <span className='text-[#6E6E6E] font-medium px-6 flex justify-between'>
                Shipping Charges{' '}
                {showLoading(
                  <>
                    <span className='text-white font-semibold xs:flex gap-x-2'>
                      {/* <ShouldRender
                        check={
                          getGlobalCountry()?.name?.toLowerCase() === 'india'
                        }
                      >
                        <p className='line-through'>
                          {amount?.currency} {ShippingPrice}
                        </p>{' '}
                      </ShouldRender> */}
                      <p className='text-[#FFC700] uppercase'>Free</p>
                    </span>
                  </>,
                  loading,
                )}
              </span>
            </ShouldRender>

            <div className='flex justify-between px-6 text-sm'>
              <span className='text-[#6E6E6E] font-semibold'>
                Total Discount
              </span>
              {showLoading(
                <>
                  <span className='font-semibold text-green-500 text-base'>
                    - {amount.currency}{' '}
                    {Math.abs(
                      amount?.actualCartTotal -
                        parseFormattedNumber(amount?.total?.toString()),
                    ).toFixed(2)}
                  </span>
                </>,
                loading,
              )}
            </div>
            <Divider
              style={{
                margin: '4px 0',
                color: '#1C1C1C',
                border: '1px solid #1C1C1C',
              }}
              className='text-cartBgColor bg-cartBgColor'
            />
            <span className='flex justify-between font-medium px-6 text-white '>
              To Pay{' '}
              {showLoading(
                <>
                  <p className='text-lg font-semibold'>
                    {amount?.currency}{' '}
                    {formatPrice(
                      parseFormattedNumber(amount?.total?.toString()),
                    )}
                  </p>
                </>,
                loading,
              )}
            </span>
          </div>
        </div>
      </div>
      <div
        className={clsx(
          !screens.lg && 'fixed bottom-0 left-0',
          'w-full lg:pb-5',
        )}
      >
        <div className='bg-bgColor border-t-[1px] lg:border-none border-[#2A2A2A] md:px-6 items-center translate-y-1 p-2 xs:p-3 lg:p-0  gap-x-2 flex'>
          <ShouldRender check={!screens.lg}>
            <div className='w-full flex flex-col  text-white no-select'>
              <ShouldRender check={savingsPercentage}>
                <span className='bg-[#002618] text-[#00A86B] pb-1 flex items-end gap-x-1 font-semibold w-fit px-2 rounded-md py-1'>
                  <div className='flex items-center -translate-y-[4px]'>
                    <motion.div
                      animate={{
                        y: [0, -0.4, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        repeatType: 'mirror',
                      }}
                    >
                      <ArrowRightIcon
                        className='w-2.5 rotate-90'
                        style={{ strokeWidth: '20px' }}
                      />
                    </motion.div>
                  </div>
                  <p className='text-[10px]'>{savingsPercentage}%</p>
                  <p className='uppercase text-[9px]'>saved</p>
                </span>
              </ShouldRender>

              <span className='flex items-end gap-x-2'>
                <p className='text-base font-semibold'>
                  {amount?.currency}
                  {parseFormattedNumber(amount.total?.toString())}
                </p>
                <ShouldRender check={amount.actualCartTotal}>
                  <p
                    className='line-through text-sm font-thin text-textGray'
                    style={{ textDecorationColor: '#7D7D7D' }}
                  >
                    {amount?.currency}
                    {amount.actualCartTotal}
                  </p>
                </ShouldRender>
              </span>
              <span
                onClick={handleScrollToBottom}
                className='text-[10px] xs:text-xs whitespace-nowrap text-[#0055D6] font-medium hover:underline no-select'
              >
                View Price Details
              </span>
            </div>
          </ShouldRender>
          <div className='w-full  md:mt-2 py-1'>
            <ButtonDefault
              size={screens.md ? 4 : 1}
              rounded={false}
              id='PROCEED-TO-CHECKOUT'
              variant={ButtonVariants.PRIMARY}
              enableScaling={false}
              onClick={() => {
                setCurrentStep(1);
                router.push({
                  pathname: process.env.NEXT_PUBLIC_CART_ROUTE || '/cart',
                  query: { cartStep: 1, cartId: cartData?.[0]?.cartId },
                });
                setCouponMessage({
                  message: '',
                  type: '',
                });
                trackBeginCheckoutCartEvent({
                  checkoutData: {
                    currency: amount?.currencyCode || 'INR',
                    currency_code: amount?.currencyCode,
                    value: amount?.total || 0,
                    coupon:
                      amount?.couponCode
                        .map((coupon) => coupon.coupon_code)
                        .join(', ') || '',
                    items: cartData.map((item) => ({
                      item_id: item.id,
                      item_name: item.name,
                      item_brand: 'Zebralearn',
                      coupon: '',
                      discount: Number(amount?.discount) || 0,
                      price: item.price,
                      quantity: Number(item.quantity),
                    })),
                    cartId: cartData?.[0]?.cartId,
                  },
                  userData,
                  pathname: router.pathname,
                });
              }}
              shimmerButton={true}
              className='w-full text-center text-sm p-3'
            >
              <span className='md:px-1 py-[3px] text-sm md:text-base font-semibold whitespace-nowrap'>
                Proceed{!isBelow370 && ' to Checkout'} (
                <AnimatePresence mode='wait'>
                  <motion.span
                    key={totalItems}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.3 }}
                    className='inline-block'
                  >
                    {totalItems}
                  </motion.span>
                </AnimatePresence>{' '}
                items)
              </span>
            </ButtonDefault>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step_1_OrderCard;
